import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import EmployeeDashboard from './EmployeeDashboard';
import AgentTicketDashboard from './AgentTickets';
import useIsMobile from '../../utils/useIsMobile';

// ── Widget definitions ──────────────────────────────────────────────────────
const WIDGET_DEFS = [
  { id: 'stats',     label: 'Key Stats' },
  { id: 'recent',    label: 'Recent Tickets' },
  { id: 'priority',  label: 'Tickets by Priority' },
  { id: 'status',    label: 'Tickets by Status' },
  { id: 'agents',    label: 'Agent Workload' },
  { id: 'sla',       label: 'SLA Overview' },
];

const DEFAULT_LAYOUT = ['stats', 'recent', 'priority', 'status', 'agents', 'sla'];

const PRIORITY_COLORS = { critical: '#dc2626', high: '#ea580c', medium: '#ca8a04', low: '#0B6623' };
const STATUS_COLORS   = { open: 'var(--status-open-color)', in_progress: 'var(--status-progress-color)', resolved: 'var(--status-resolved-color)', closed: 'var(--status-closed-color)' };

function fmt(h) {
  if (h == null || isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
}

// ── Resize helpers ──────────────────────────────────────────────────────────
function useDrag(onDrag, cursor) {
  const dragging = useRef(false);
  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = cursor;
    document.body.style.userSelect = 'none';
    const onMove = (ev) => { if (dragging.current) onDrag(ev); };
    const onUp   = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [onDrag, cursor]);
  return onMouseDown;
}

// Horizontal split row with drag divider
function ResizableRow({ left, right, storageKey, defaultLeftPct = 50 }) {
  const isMobile = useIsMobile();
  const [pct, setPct] = useState(() => {
    try { return Number(localStorage.getItem(storageKey)) || defaultLeftPct; } catch { return defaultLeftPct; }
  });
  const ref = useRef(null);
  const onDrag = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const next = Math.min(80, Math.max(20, ((e.clientX - r.left) / r.width) * 100));
    setPct(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  }, [storageKey]);
  const onMouseDown = useDrag(onDrag, 'col-resize');

  // On mobile: stack the two widgets vertically, no drag handle
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>{left}</div>
        <div>{right}</div>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ display: 'flex', alignItems: 'stretch' }}>
      <div style={{ width: `calc(${pct}% - 12px)`, minWidth: 0, flexShrink: 0 }}>{left}</div>
      {/* vertical divider handle */}
      <div onMouseDown={onMouseDown}
        style={{ width: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'col-resize', zIndex: 1 }}>
        <div style={{ width: 4, height: 48, borderRadius: 2, background: 'var(--n-200)', transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#0B6623'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--n-200)'} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{right}</div>
    </div>
  );
}

// Height-resizable wrapper — wraps any widget card with a bottom drag handle
function ResizableHeight({ children, storageKey, defaultHeight = 'auto' }) {
  const [height, setHeight] = useState(() => {
    try { const v = localStorage.getItem(storageKey); return v ? Number(v) : null; } catch { return null; }
  });
  const ref = useRef(null);
  const onDrag = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const next = Math.max(120, e.clientY - r.top);
    setHeight(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  }, [storageKey]);
  const onMouseDown = useDrag(onDrag, 'row-resize');

  return (
    <div ref={ref} style={{ position: 'relative', height: height ? `${height}px` : defaultHeight, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</div>
      {/* horizontal divider handle at bottom */}
      <div onMouseDown={onMouseDown}
        style={{ height: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'row-resize', flexShrink: 0 }}>
        <div style={{ width: 48, height: 4, borderRadius: 2, background: 'var(--n-200)', transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#0B6623'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--n-200)'} />
      </div>
    </div>
  );
}

// ── Individual Widgets ──────────────────────────────────────────────────────

function StatsWidget({ stats, tickets = [] }) {
  const isMobile = useIsMobile();
  const nav = useNavigate();
  const [drill, setDrill] = React.useState(null); // { label, rows }
  const now = new Date();
  const isOpenish = t => !['resolved', 'closed'].includes(t.status);
  const items = [
    { label: 'Open',        value: stats?.open ?? '—',        color: 'var(--status-open-color)', bg: 'var(--status-open-bg)', match: t => t.status === 'open' },
    { label: 'In Progress', value: stats?.in_progress ?? '—', color: 'var(--status-progress-color)', bg: 'var(--status-progress-bg)', match: t => t.status === 'in_progress' },
    { label: 'Resolved',    value: stats?.resolved ?? '—',    color: 'var(--status-resolved-color)', bg: 'var(--status-resolved-bg)', match: t => t.status === 'resolved' },
    { label: 'SLA Breached',value: stats?.breached ?? '—',    color: '#dc2626', bg: 'var(--red-50)', match: t => t.due_at && isOpenish(t) && new Date(t.due_at) < now && !t.sla_paused },
    { label: 'Pending – SLA stopped', value: stats?.sla_stopped ?? '—', color: '#92400e', bg: 'var(--amber-50)', match: t => t.sla_paused && isOpenish(t) },
  ];
  function openDrill(s) {
    const rows = tickets.filter(s.match);
    setDrill({ label: s.label, rows });
  }
  return (
    <>
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12 }}>
      {items.map(s => (
        <div key={s.label} onClick={() => openDrill(s)} title="Click to view tickets"
          style={{ background: s.bg, borderRadius: 12, padding: '16px 14px', textAlign: 'center', border: `1px solid ${s.color}22`, cursor: 'pointer', transition: 'transform .1s' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
          <div style={{ fontSize: 48, fontWeight: 900, color: s.color, fontFamily: 'Impact, Arial Narrow, sans-serif', lineHeight: 1 }}>{s.value}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5 }}>{s.label}</div>
        </div>
      ))}
    </div>

    {drill && (
      <div onClick={() => setDrill(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={e => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: 720, maxHeight: '82vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{drill.label} · {drill.rows.length}</span>
            <button onClick={() => setDrill(null)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ overflowY: 'auto', padding: 8 }}>
            {drill.rows.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: 30, fontStyle: 'italic' }}>No tickets.</p>
            ) : drill.rows.map(t => (
              <div key={t.id} onClick={() => { setDrill(null); nav(`/admin/tickets/${t.id}`); }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', borderRadius: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-app)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, background: '#0B6623', color: '#fff', padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>{t.ticket_number}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t.customer_name || ''}{t.product_name ? ` · ${t.product_name}` : ''}</div>
                </div>
                <span className={`badge badge-${t.priority}`} style={{ fontSize: 10, flexShrink: 0 }}>{t.priority}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function RecentWidget({ tickets }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
      {(tickets || []).slice(0, 8).map(t => (
        <Link key={t.id} to={`/admin/tickets/${t.id}`} style={{ textDecoration: 'none' }}>
          <div style={{ padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-resolved-text)' }}>{t.ticket_number}</span>
              <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 999, background: (PRIORITY_COLORS[t.priority] || '#888') + '18', color: PRIORITY_COLORS[t.priority] || '#888', fontWeight: 700, textTransform: 'uppercase' }}>{t.priority}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{t.customer_name} · {new Date(t.created_at).toLocaleDateString()}</div>
          </div>
        </Link>
      ))}
      {(!tickets || tickets.length === 0) && <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No tickets</p>}
    </div>
  );
}

function PriorityWidget({ tickets }) {
  const counts = { critical: 0, high: 0, medium: 0, low: 0 };
  (tickets || []).forEach(t => { if (counts[t.priority] !== undefined) counts[t.priority]++; });
  const total = Math.max(Object.values(counts).reduce((a, b) => a + b, 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {Object.entries(counts).map(([p, c]) => {
        const pct = (c / total) * 100;
        return (
          <div key={p}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'capitalize', display: 'block', marginBottom: 4 }}>{p}</span>
            <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 6, height: 18 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: PRIORITY_COLORS[p], borderRadius: 6, transition: 'width .4s' }} />
              {c > 0 && (
                <span style={{ position: 'absolute', left: `calc(${pct}% - 2px)`, top: '50%', transform: 'translate(-100%, -50%)', fontSize: 11, fontWeight: 800, color: '#fff', paddingRight: 4, pointerEvents: 'none' }}>{c}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusWidget({ tickets }) {
  const counts = { open: 0, in_progress: 0, resolved: 0, closed: 0 };
  (tickets || []).forEach(t => { if (counts[t.status] !== undefined) counts[t.status]++; });
  const total = Math.max(Object.values(counts).reduce((a, b) => a + b, 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {Object.entries(counts).map(([s, c]) => {
        const pct = (c / total) * 100;
        return (
          <div key={s}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'capitalize', display: 'block', marginBottom: 4 }}>{s.replace('_', ' ')}</span>
            <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 6, height: 18 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: STATUS_COLORS[s], borderRadius: 6, transition: 'width .4s' }} />
              {c > 0 && (
                <span style={{ position: 'absolute', left: `calc(${pct}% - 2px)`, top: '50%', transform: 'translate(-100%, -50%)', fontSize: 11, fontWeight: 800, color: '#fff', paddingRight: 4, pointerEvents: 'none' }}>{c}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AgentWidget({ agents }) {
  const list = (agents || []).slice(0, 10);
  const max = Math.max(...list.map(a => a.ticket_count), 1);
  if (list.length === 0) return <p style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No agents</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {list.map(a => {
        const pct = (a.ticket_count / max) * 100;
        const color = a.ticket_count > 5 ? '#dc2626' : '#0B6623';
        return (
          <div key={a.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg,#0B6623,#19a843)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                {a.name?.[0]?.toUpperCase()}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{a.name}</span>
            </div>
            <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 6, height: 18 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6, transition: 'width .4s' }} />
              {a.ticket_count > 0 && (
                <span style={{ position: 'absolute', left: `calc(${pct}% - 2px)`, top: '50%', transform: 'translate(-100%, -50%)', fontSize: 11, fontWeight: 800, color: '#fff', paddingRight: 4, pointerEvents: 'none' }}>{a.ticket_count}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Widget Card Wrapper ─────────────────────────────────────────────────────
function SlaWidget({ tickets }) {
  const list = tickets || [];
  const now  = new Date();
  const total    = list.length;
  const complied = list.filter(t => t.due_at && ['resolved','closed'].includes(t.status) && new Date(t.resolved_at || t.closed_at) <= new Date(t.due_at)).length;
  const breached = list.filter(t => t.due_at && (
    (!['resolved','closed'].includes(t.status) && new Date(t.due_at) < now) ||
    (['resolved','closed'].includes(t.status) && t.resolved_at && new Date(t.resolved_at) > new Date(t.due_at))
  )).length;

  const rows = [
    { label: 'Total Tickets', value: total,    color: '#0B6623' },
    { label: 'SLA Complied',  value: complied, color: '#0284c7' },
    { label: 'SLA Breached',  value: breached, color: '#dc2626' },
  ];
  const max = Math.max(total, 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map(r => {
        const pct = (r.value / max) * 100;
        return (
          <div key={r.label}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{r.label}</span>
            <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 6, height: 18 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: r.color, borderRadius: 6, transition: 'width .4s' }} />
              {r.value > 0 && (
                <span style={{ position: 'absolute', left: `calc(${pct}% - 2px)`, top: '50%', transform: 'translate(-100%, -50%)', fontSize: 11, fontWeight: 800, color: '#fff', paddingRight: 4, pointerEvents: 'none' }}>{r.value}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WidgetConfigPanel({ layout, onReorder, onToggle }) {
  const dragId = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  function handleDragStart(e, id) {
    dragId.current = id;
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, id) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragId.current) setDragOver(id);
  }

  function handleDrop(e, targetId) {
    e.preventDefault();
    const from = dragId.current;
    if (!from || from === targetId) { setDragOver(null); return; }
    const nl = [...layout];
    const fromIdx = nl.indexOf(from);
    const toIdx   = nl.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) { setDragOver(null); return; }
    nl.splice(fromIdx, 1);
    nl.splice(toIdx, 0, from);
    onReorder(nl);
    setDragOver(null);
  }

  function handleDragEnd() {
    dragId.current = null;
    setDragOver(null);
  }

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--action-hover)', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Widget Configuration</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>Drag to reorder · 👁 to show/hide</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {WIDGET_DEFS.map((def) => {
          const visible   = layout.includes(def.id);
          const isDragTgt = dragOver === def.id;
          return (
            <div
              key={def.id}
              draggable={visible}
              onDragStart={visible ? e => handleDragStart(e, def.id) : undefined}
              onDragOver={visible ? e => handleDragOver(e, def.id) : undefined}
              onDrop={visible ? e => handleDrop(e, def.id) : undefined}
              onDragEnd={handleDragEnd}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 14px', borderRadius: 10,
                background: isDragTgt ? 'var(--n-100)' : visible ? 'var(--action-bg)' : 'var(--bg-app)',
                border: `1.5px solid ${isDragTgt ? '#0B6623' : visible ? 'var(--action-hover)' : 'var(--border-subtle)'}`,
                opacity: visible ? 1 : 0.5,
                cursor: visible ? 'grab' : 'default',
                transition: 'border-color .12s, background .12s',
              }}
            >
              {/* Drag handle */}
              {visible && (
                <span style={{ fontSize: 14, color: 'var(--text-secondary)', flexShrink: 0, userSelect: 'none' }}>⠿</span>
              )}
              {/* Eye toggle */}
              <button
                title={visible ? 'Hide widget' : 'Show widget'}
                onClick={() => onToggle(def.id, visible)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0, color: visible ? 'var(--status-resolved-text)' : 'var(--text-secondary)', flexShrink: 0 }}>
                {visible ? '👁' : '👁'}
              </button>
              {/* Label */}
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: visible ? 'var(--text-primary)' : 'var(--text-secondary)', userSelect: 'none' }}>
                {def.label}
              </span>
              <span style={{ fontSize: 11, color: visible ? 'var(--status-resolved-text)' : 'var(--text-secondary)', fontWeight: 600 }}>
                {visible ? 'Visible' : 'Hidden'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WidgetCard({ wid, data, editMode }) {
  const def = WIDGET_DEFS.find(d => d.id === wid);
  if (!def) return null;

  const renderContent = () => {
    switch (wid) {
      case 'stats':     return <StatsWidget stats={data.stats} tickets={data.tickets} />;
      case 'recent':    return <RecentWidget tickets={data.tickets} />;
      case 'priority':  return <PriorityWidget tickets={data.tickets} />;
      case 'status':    return <StatusWidget tickets={data.tickets} />;
      case 'agents':    return <AgentWidget agents={data.agents} />;
      case 'sla':       return <SlaWidget tickets={data.tickets} />;
      default: return null;
    }
  };

  return (
    <ResizableHeight storageKey={`kg_widget_h_${wid}`}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 14, border: `1px solid ${editMode ? 'var(--action-hover)' : 'var(--border-subtle)'}`, boxShadow: '0 1px 4px rgba(15,23,42,.06)', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'border-color .2s' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: editMode ? 'var(--action-bg)' : 'var(--bg-surface)', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{def.label}</span>
          {editMode && (
            <div style={{ display: 'flex', gap: 4 }}>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 16px', flex: 1, overflowY: 'auto' }}>{renderContent()}</div>
      </div>
    </ResizableHeight>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const [view, setView]         = useState('dashboard'); // 'dashboard' | 'employee' | 'agent'
  const [data, setData]         = useState({});
  const [layout, setLayout]     = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [ticketsRes, agentsRes, prefsRes] = await Promise.all([
        apiFetch('/tickets?limit=200'),
        apiFetch('/users/agents'),
        apiFetch('/analytics/dashboard-prefs').catch(() => null),
      ]);

      const tList = ticketsRes?.tickets || ticketsRes || [];
      const now   = new Date();
      const stats = {
        open:        tList.filter(t => t.status === 'open').length,
        in_progress: tList.filter(t => t.status === 'in_progress').length,
        resolved:    tList.filter(t => t.status === 'resolved').length,
        breached:    tList.filter(t => t.due_at && !['resolved','closed'].includes(t.status) && new Date(t.due_at) < now && !t.sla_paused).length,
        sla_stopped: tList.filter(t => t.sla_paused && !['resolved','closed'].includes(t.status)).length,
      };

      const agents = (agentsRes || []).map(a => ({
        ...a,
        ticket_count: tList.filter(t => t.assigned_to === a.id && !['resolved','closed'].includes(t.status)).length,
      })).sort((a, b) => b.ticket_count - a.ticket_count);

      setData({ stats, tickets: tList, agents });
      const saved = prefsRes?.layout?.length > 0 ? prefsRes.layout : DEFAULT_LAYOUT;
      const merged = [...saved, ...DEFAULT_LAYOUT.filter(w => !saved.includes(w))];
      setLayout(merged);
    } catch (_) {
      setLayout(DEFAULT_LAYOUT);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function saveLayout(newLayout) {
    setSaving(true);
    try { await apiFetch('/analytics/dashboard-prefs', { method: 'POST', body: { layout: newLayout } }); } catch (_) {}
    setSaving(false);
  }

function removeWidget(id) { setLayout(layout.filter(w => w !== id)); }
  function addWidget(id)    { if (!layout.includes(id)) setLayout([...layout, id]); }

  function handleSave()  { saveLayout(layout); setEditMode(false); }
  function handleReset() { const dl = [...DEFAULT_LAYOUT]; setLayout(dl); saveLayout(dl); setEditMode(false); }

  if (loading) return (
    <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Support Dashboard</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>Welcome back, {user?.name}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {view === 'dashboard' && saving && <span style={{ fontSize: 12, color: 'var(--status-resolved-text)', fontWeight: 600 }}>Saving…</span>}
          {view === 'dashboard' && (editMode ? (
            <>
              <button onClick={handleReset}
                style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Reset to Default
              </button>
              <button onClick={handleSave}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#0B6623', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Save Layout
              </button>
            </>
          ) : (
            <button onClick={() => setEditMode(true)}
              style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              ⚙ Customise Dashboard
            </button>
          ))}
        </div>
      </div>

      {/* Tabs — Dashboard + Employee Dashboard nested here */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 20 }}>
        {[['dashboard', '📊 Dashboard'], ['employee', '👥 Employee Dashboard'], ['agent', '🎫 Agent Tickets']].map(t => (
          <button key={t[0]} onClick={() => setView(t[0])}
            style={{ padding: '10px 22px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
              color: view === t[0] ? '#0B6623' : 'var(--text-secondary)',
              borderBottom: `3px solid ${view === t[0] ? '#0B6623' : 'transparent'}`, marginBottom: -2, transition: 'all .15s' }}>
            {t[1]}
          </button>
        ))}
      </div>

      {view === 'employee' ? (
        <div style={{ margin: '0 -28px -28px' }}><EmployeeDashboard /></div>
      ) : view === 'agent' ? (
        <div style={{ margin: '0 -28px -28px' }}><AgentTicketDashboard /></div>
      ) : (
      <>
      {/* Widget configuration panel */}
      {editMode && (
        <WidgetConfigPanel layout={layout || []} onReorder={setLayout} onToggle={(id, visible) => visible ? removeWidget(id) : addWidget(id)} />
      )}

      {/* Widgets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {(() => {
          const items = layout || [];
          const hasPriority  = items.includes('priority');
          const hasStatus    = items.includes('status');
          const hasAgents    = items.includes('agents');
          const hasSla       = items.includes('sla');
          const pairRow      = hasPriority && hasStatus;
          const agentSlaRow  = hasAgents && hasSla;
          const rendered    = new Set();
          return items.map((wid) => {
            if (rendered.has(wid)) return null;
            if (pairRow && (wid === 'priority' || wid === 'status')) {
              rendered.add('priority'); rendered.add('status');
              return (
                <ResizableRow key="priority-status-row" storageKey="kg_dashboard_priority_pct"
                  left={<WidgetCard wid="priority" data={data} editMode={editMode} />}
                  right={<WidgetCard wid="status" data={data} editMode={editMode} />}
                />
              );
            }
            rendered.add(wid);
            if (agentSlaRow && (wid === 'agents' || wid === 'sla')) {
              rendered.add('agents'); rendered.add('sla');
              return (
                <ResizableRow key="agents-sla-row" storageKey="kg_dashboard_agents_pct"
                  left={<WidgetCard wid="agents" data={data} editMode={editMode} />}
                  right={<WidgetCard wid="sla" data={data} editMode={editMode} />}
                />
              );
            }
            return <WidgetCard key={wid} wid={wid} data={data} editMode={editMode} />;
          });
        })()}
      </div>
      </>
      )}
    </div>
  );
}
